/**
 * Sortable Filterable Table Header Component
 * 
 * Reusable table header with integrated filtering and sorting for each column.
 * Shows filter dropdowns and sort indicators in the table headers.
 * 
 * @module SortableFilterableTableHeader
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Filter, ChevronDown, ChevronUp, X, ArrowUpDown } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';

export type SortDirection = 'asc' | 'desc' | null;

export interface ColumnFilter {
  column: string;
  value: string | string[] | null; // Support single value or array of values for multi-select
  dateRange?: {
    from: string | null; // ISO date string
    to: string | null; // ISO date string
  } | null;
}

export interface ColumnSort {
  column: string;
  direction: SortDirection;
}

interface SortableFilterableTableHeaderProps {
  columns: Array<{
    key: string;
    label: string;
    filterable?: boolean;
    sortable?: boolean;
    filterType?: 'default' | 'dateRange'; // Custom filter type
    dbField?: string; // Database field name (if different from key)
  }>;
  data: any[];
  onFilterChange: (filters: ColumnFilter[]) => void;
  onSortChange: (sort: ColumnSort | null) => void;
  currentFilters?: ColumnFilter[];
  currentSort?: ColumnSort | null;
  scrollContainerId?: string;
  renderAsRow?: boolean; // If true, returns just <tr> instead of <thead><tr>
  filterValuesFromDb?: Record<string, string[]>; // Optional: filter values loaded from database
  onLoadFilterValues?: (columnKey: string, dbField: string, searchTerm?: string) => Promise<string[]>; // Callback to load filter values from DB (with optional search term)
}

const HeaderCell = styled.th`
  padding: 8px 16px;
  text-align: left;
  font-weight: 600;
  color: ${ThemeColors.text.primary};
  border-bottom: 1px solid ${ThemeColors.border.light};
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  position: relative;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-width: fit-content;
  width: auto;
  
  &:first-child {
    padding-left: 24px;
  }
  
  &:last-child {
    padding-right: 24px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Spacing.xs};
  width: 100%;
`;

const HeaderLabel = styled.span`
  flex-shrink: 1;
  font-weight: ${Typography.fontWeight.semiBold};
  line-height: 1.3;
  display: block;
  
  /* Prevent automatic word breaking */
  word-break: normal;
  overflow-wrap: normal;
  white-space: pre-line;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.xs};
  flex-shrink: 0;
  margin-left: ${Spacing.xs};
  padding-top: 2px;
`;

const FilterIcon = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: ${props => props.active ? ThemeColors.primary.main : 'transparent'};
  color: ${props => props.active ? '#fff' : ThemeColors.text.secondary};
  border-radius: ${BorderRadius.sm};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  flex-shrink: 0;

  &:hover {
    background: ${props => props.active ? ThemeColors.primary.dark : ThemeColors.gray[100]};
    color: ${props => props.active ? '#fff' : ThemeColors.text.primary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SortButton = styled.button<{ active: boolean; direction: SortDirection }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: ${props => props.active ? ThemeColors.primary.main : 'transparent'};
  color: ${props => props.active ? '#fff' : ThemeColors.text.secondary};
  border-radius: ${BorderRadius.sm};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  flex-shrink: 0;

  &:hover {
    background: ${props => props.active ? ThemeColors.primary.dark : ThemeColors.gray[100]};
    color: ${props => props.active ? '#fff' : ThemeColors.text.primary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const FilterDropdown = styled.div<{ isOpen: boolean; top: number; left: number; minWidth: number }>`
  position: fixed;
  top: ${props => `${props.top}px`};
  left: ${props => `${props.left}px`};
  min-width: ${props => `${props.minWidth}px`};
  max-width: 320px;
  background-color: #fff;
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  box-shadow: ${Shadows.lg};
  z-index: 2000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  max-height: 300px;
  overflow-y: auto;
  margin-top: 2px;
`;

const FilterSearch = styled.input`
  width: 100%;
  padding: ${Spacing.sm};
  border: none;
  border-bottom: 1px solid ${ThemeColors.border.light};
  font-size: ${Typography.fontSize.sm};
  background-color: ${ThemeColors.gray[50]};

  &:focus {
    outline: none;
    background-color: #fff;
  }
`;

const FilterOptions = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const FilterOption = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${Spacing.sm};
  border: none;
  background: ${props => props.selected ? ThemeColors.primary.light : 'transparent'};
  color: ${props => props.selected ? ThemeColors.primary.main : ThemeColors.text.primary};
  text-align: left;
  cursor: pointer;
  font-size: ${Typography.fontSize.sm};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:hover {
    background-color: ${props => props.selected ? ThemeColors.primary.light : ThemeColors.gray[50]};
  }

  &:first-child {
    border-radius: ${BorderRadius.md} ${BorderRadius.md} 0 0;
  }
`;

const FilterFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.sm};
  border-top: 1px solid ${ThemeColors.border.light};
  gap: ${Spacing.xs};
`;

const ClearFilterButton = styled.button`
  flex: 1;
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.light};
  background-color: #fff;
  color: ${ThemeColors.text.secondary};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:hover {
    background-color: ${ThemeColors.gray[100]};
    color: ${ThemeColors.text.primary};
    border-color: ${ThemeColors.border.main};
  }
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background-color: ${ThemeColors.error.main};
  color: white;
  border-radius: ${BorderRadius.full};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  margin-left: 4px;
`;

const DateRangeContainer = styled.div`
  padding: ${Spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
  min-width: 280px;
`;

const DateRangeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const DateRangeLabel = styled.label`
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DateInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  font-family: ${Typography.fontFamily.base};
  color: ${ThemeColors.text.primary};
  background-color: white;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    transition: opacity ${Transitions.duration.short} ${Transitions.easing.easeInOut};

    &:hover {
      opacity: 1;
    }
  }
`;

const DateRangeButtons = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  margin-top: ${Spacing.xs};
`;

export const SortableFilterableTableHeader: React.FC<SortableFilterableTableHeaderProps> = ({
  columns,
  data,
  onFilterChange,
  onSortChange,
  currentFilters = [],
  currentSort = null,
  scrollContainerId,
  renderAsRow = false,
  filterValuesFromDb = {},
  onLoadFilterValues
}) => {
  const { t } = useTranslation();
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState<Record<string, string>>({});
  const [dateRangeFilters, setDateRangeFilters] = useState<Record<string, { from: string; to: string }>>({});
  const [pendingFilterSelections, setPendingFilterSelections] = useState<Record<string, string[]>>({});
  const [dropdownPosition, setDropdownPosition] = useState<{ left: number; top: number; width: number } | null>(null);
  const [loadingFilterValues, setLoadingFilterValues] = useState<Record<string, boolean>>({});
  const [loadedFilterValues, setLoadedFilterValues] = useState<Record<string, string[]>>(filterValuesFromDb);
  const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const scrollParentRef = useRef<HTMLElement | null>(null);

  // Extract unique values for each filterable column from loaded data (fallback)
  const uniqueValuesFromData = useMemo(() => {
    const values: Record<string, string[]> = {};
    
    columns.forEach(column => {
      if (column.filterable) {
        const columnValues = data
          .map(item => {
            // Handle nested property access (e.g., 'customerNo', 'address', etc.)
            const value = item[column.key];
            return value ? String(value).trim() : '';
          })
          .filter(value => value !== '')
          .sort();
        
        // Get unique values
        values[column.key] = Array.from(new Set(columnValues));
      }
    });
    
    return values;
  }, [data, columns]);

  // Use database values if available, otherwise fall back to data values
  // Always show data values immediately, then replace with database values when loaded
  const uniqueValues = useMemo(() => {
    const values: Record<string, string[]> = {};
    
    columns.forEach(column => {
      if (column.filterable) {
        // If database values are loaded, use them (they're complete)
        // Otherwise, use data values as a fallback (shows what's currently loaded)
        if (loadedFilterValues[column.key] && loadedFilterValues[column.key].length > 0) {
          values[column.key] = loadedFilterValues[column.key];
        } else {
          // Use data values as fallback - shows at least the currently loaded stores
          values[column.key] = uniqueValuesFromData[column.key] || [];
        }
      }
    });
    
    return values;
  }, [loadedFilterValues, uniqueValuesFromData, columns]);

  // Load filter values from database when filter is opened or when searching
  // IMPORTANT: Always loads from entire database, not just displayed stores
  // When searchTerm is provided, queries database with search term
  const loadFilterValuesFromDb = useCallback(async (columnKey: string, searchTerm?: string, forceReload: boolean = false) => {
    const column = columns.find(c => c.key === columnKey);
    if (!column || !column.filterable || !onLoadFilterValues) {
      console.log(`Skipping load for ${columnKey}:`, { 
        column: !!column, 
        filterable: column?.filterable, 
        hasCallback: !!onLoadFilterValues 
      });
      return;
    }
    
    const searchLower = searchTerm?.toLowerCase().trim();
    
    // For search queries, always reload (don't use cache)
    // For non-search queries, check if already loaded (unless forcing reload)
    if (!searchLower && !forceReload && loadedFilterValues[columnKey] && loadedFilterValues[columnKey].length > 0) {
      console.log(`Filter values already loaded for ${columnKey}:`, loadedFilterValues[columnKey].length, 'values');
      return;
    }
    
    // Check if already loading
    if (loadingFilterValues[columnKey]) {
      console.log(`Filter values already loading for ${columnKey}`);
      return;
    }
    
    const dbField = column.dbField || column.key;
    if (searchLower) {
      console.log(`[SortableFilterableTableHeader] Searching filter values in DATABASE for ${columnKey} (dbField: ${dbField}) with search: "${searchTerm}"`);
    } else {
      console.log(`[SortableFilterableTableHeader] Loading filter values from DATABASE for ${columnKey} (dbField: ${dbField})`);
    }
    setLoadingFilterValues(prev => ({ ...prev, [columnKey]: true }));
    
    try {
      const values = await onLoadFilterValues(columnKey, dbField, searchTerm);
      if (searchLower) {
        console.log(`[SortableFilterableTableHeader] ✓ Found ${values.length} matching filter values from DATABASE for ${columnKey} with search "${searchTerm}":`, values.slice(0, 10));
        // When search results load, clear pending selections so "All [search term]" is unselected
        // User can then click "All [search term]" to select all results
        setPendingFilterSelections(prev => ({
          ...prev,
          [columnKey]: []
        }));
      } else {
        console.log(`[SortableFilterableTableHeader] ✓ Loaded ${values.length} filter values from DATABASE for ${columnKey}:`, values.slice(0, 10));
      }
      setLoadedFilterValues(prev => ({ ...prev, [columnKey]: values }));
    } catch (error) {
      console.error(`[SortableFilterableTableHeader] Failed to load filter values for ${columnKey}:`, error);
      // Set empty array on error so we don't keep trying
      setLoadedFilterValues(prev => ({ ...prev, [columnKey]: [] }));
    } finally {
      setLoadingFilterValues(prev => ({ ...prev, [columnKey]: false }));
    }
  }, [columns, onLoadFilterValues, loadedFilterValues, loadingFilterValues]);

  // Get filtered unique values based on search
  // IMPORTANT: When searching, values are already filtered by database query
  // Database values are loaded with search term applied, so no additional filtering needed
  const getFilteredValues = (columnKey: string): string[] => {
    const searchTerm = filterSearch[columnKey]?.toLowerCase().trim() || '';
    const values = uniqueValues[columnKey] || [];
    const isLoading = loadingFilterValues[columnKey];
    
    // If database values are loaded, use them directly
    // When searchTerm was provided, values are already filtered by database query
    // When no searchTerm, values contain all unique values
    if (values.length > 0) {
      // Database values are already filtered if searchTerm was used in loadFilterValuesFromDb
      // No need to filter again - just return the values
      return values;
    }
    
    // If database values are loading, don't show fallback values when searching
    // This prevents showing incomplete results (only displayed stores)
    // Wait for database values to load for accurate search results
    if (isLoading && searchTerm) {
      // Return empty array while loading - database values will be available soon
      return [];
    }
    
    // Only use fallback values if:
    // 1. Not loading database values
    // 2. No search term (showing all values is okay)
    // 3. Database values aren't available yet
    if (!isLoading && uniqueValuesFromData[columnKey]?.length > 0) {
      const dataValues = uniqueValuesFromData[columnKey];
      if (!searchTerm) return dataValues;
      // When searching with fallback values, show a warning that results may be incomplete
      console.warn(`[SortableFilterableTableHeader] Searching with fallback values for ${columnKey}. Results may be incomplete. Database values still loading.`);
      return dataValues.filter(value => 
        value.toLowerCase().includes(searchTerm)
      );
    }
    
    return [];
  };

  // Handle filter toggle
  const updateDropdownPosition = useCallback((columnKey: string) => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SortableFilterableTableHeader.tsx:444',message:'updateDropdownPosition called',data:{columnKey,hasCell:!!headerRefs.current[columnKey],allRefs:Object.keys(headerRefs.current)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    const cell = headerRefs.current[columnKey];
    if (!cell) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SortableFilterableTableHeader.tsx:449',message:'Cell ref not found',data:{columnKey,availableRefs:Object.keys(headerRefs.current)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      // Retry after a short delay to allow ref to be set
      setTimeout(() => {
        const retryCell = headerRefs.current[columnKey];
        if (retryCell) {
          const rect = retryCell.getBoundingClientRect();
          setDropdownPosition({
            left: rect.left + window.scrollX,
            top: rect.bottom + window.scrollY + 4,
            width: rect.width
          });
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SortableFilterableTableHeader.tsx:456',message:'Retry successful - dropdown position set',data:{columnKey,left:rect.left,top:rect.bottom,width:rect.width},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
          // #endregion
        }
      }, 10);
      return;
    }

    const rect = cell.getBoundingClientRect();
    setDropdownPosition({
      left: rect.left + window.scrollX,
      top: rect.bottom + window.scrollY + 4,
      width: rect.width
    });
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SortableFilterableTableHeader.tsx:470',message:'Dropdown position set',data:{columnKey,left:rect.left + window.scrollX,top:rect.bottom + window.scrollY + 4,width:rect.width},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion

    if (scrollContainerId) {
      const scrollParent = cell.closest<HTMLElement>(`[data-scroll-container="${scrollContainerId}"]`);
      scrollParentRef.current = scrollParent || null;
    } else {
      scrollParentRef.current = null;
    }
  }, [scrollContainerId]);

  const handleFilterToggle = (columnKey: string) => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SortableFilterableTableHeader.tsx:465',message:'handleFilterToggle called',data:{columnKey,currentOpenFilter:openFilterColumn,willClose:openFilterColumn === columnKey},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    if (openFilterColumn === columnKey) {
      setOpenFilterColumn(null);
      setFilterSearch({ ...filterSearch, [columnKey]: '' });
      setDropdownPosition(null);
      scrollParentRef.current = null;
      // Clear pending selections when closing
      setPendingFilterSelections(prev => {
        const updated = { ...prev };
        delete updated[columnKey];
        return updated;
      });
    } else {
      setOpenFilterColumn(columnKey);
      setFilterSearch({ ...filterSearch, [columnKey]: '' });
      
      // Initialize pending selections with current filter values
      const currentFilter = getCurrentFilterValue(columnKey);
      if (currentFilter) {
        const currentValues = Array.isArray(currentFilter) ? currentFilter : [currentFilter];
        setPendingFilterSelections(prev => ({
          ...prev,
          [columnKey]: currentValues
        }));
      } else {
        setPendingFilterSelections(prev => ({
          ...prev,
          [columnKey]: []
        }));
      }
      
      // Load filter values from database if available (without search term initially)
      loadFilterValuesFromDb(columnKey, undefined, false);
      
      // Initialize date range filter with current values if they exist
      const currentDateRange = getCurrentDateRangeFilter(columnKey);
      if (currentDateRange) {
        setDateRangeFilters(prev => ({
          ...prev,
          [columnKey]: {
            from: currentDateRange.from || '',
            to: currentDateRange.to || ''
          }
        }));
      }
      
      // Use requestAnimationFrame to ensure DOM is updated before calculating position
      requestAnimationFrame(() => {
        updateDropdownPosition(columnKey);
      });
    }
  };

  // Handle filter selection (supports multi-select with checkboxes)
  // Now updates pending selections instead of applying immediately
  const handleFilterSelect = (columnKey: string, value: string | null) => {
    const currentPending = pendingFilterSelections[columnKey] || [];
    const searchTerm = filterSearch[columnKey]?.trim();
    const currentFilterValues = uniqueValues[columnKey] || [];
    
    if (value === null) {
      // "All" or "All [search term]" clicked
      if (searchTerm && currentFilterValues.length > 0) {
        // "All [search term]" - select all filtered results
        setPendingFilterSelections(prev => ({
          ...prev,
          [columnKey]: [...currentFilterValues]
        }));
      } else {
        // "All" (no search) - clear pending selections
      setPendingFilterSelections(prev => ({
        ...prev,
        [columnKey]: []
      }));
      }
      return;
    }
    
    // Toggle value in pending selections
    if (currentPending.includes(value)) {
      // Remove value
      const newPending = currentPending.filter(v => v !== value);
      setPendingFilterSelections(prev => ({
        ...prev,
        [columnKey]: newPending
      }));
    } else {
      // Add value
      setPendingFilterSelections(prev => ({
        ...prev,
        [columnKey]: [...currentPending, value]
      }));
    }
  };

  // Apply pending filter selections
  const applyPendingFilter = (columnKey: string) => {
    const pendingValues = pendingFilterSelections[columnKey] || [];
    const newFilters = currentFilters.filter(f => f.column !== columnKey);
    
    if (pendingValues.length > 0) {
      // Apply the pending selections
      const filterValue = pendingValues.length === 1 ? pendingValues[0] : pendingValues;
      newFilters.push({ column: columnKey, value: filterValue });
    }
    
    onFilterChange(newFilters);
    
    // Close the dropdown and clear pending selections
    setOpenFilterColumn(null);
    setFilterSearch({ ...filterSearch, [columnKey]: '' });
    setDropdownPosition(null);
    scrollParentRef.current = null;
    setPendingFilterSelections(prev => {
      const updated = { ...prev };
      delete updated[columnKey];
      return updated;
    });
  };

  // Handle date range filter change
  const handleDateRangeChange = (columnKey: string, from: string, to: string) => {
    setDateRangeFilters(prev => ({
      ...prev,
      [columnKey]: { from, to }
    }));
  };

  // Apply date range filter
  const applyDateRangeFilter = (columnKey: string) => {
    const dateRange = dateRangeFilters[columnKey];
    const newFilters = currentFilters.filter(f => f.column !== columnKey);
    
    if (dateRange && (dateRange.from || dateRange.to)) {
      newFilters.push({
        column: columnKey,
        value: null,
        dateRange: {
          from: dateRange.from || null,
          to: dateRange.to || null
        }
      });
    }
    
    onFilterChange(newFilters);
    setOpenFilterColumn(null);
    setDateRangeFilters(prev => {
      const updated = { ...prev };
      delete updated[columnKey];
      return updated;
    });
  };

  // Clear date range filter
  const clearDateRangeFilter = (columnKey: string) => {
    const newFilters = currentFilters.filter(f => f.column !== columnKey);
    onFilterChange(newFilters);
    setDateRangeFilters(prev => {
      const updated = { ...prev };
      delete updated[columnKey];
      return updated;
    });
  };

  // Handle sort toggle
  const handleSortToggle = (columnKey: string) => {
    const isCurrentlySorted = currentSort?.column === columnKey;
    let newDirection: SortDirection = 'asc';
    
    if (isCurrentlySorted) {
      if (currentSort?.direction === 'asc') {
        newDirection = 'desc';
      } else if (currentSort?.direction === 'desc') {
        newDirection = null;
      }
    }
    
    if (newDirection === null) {
      onSortChange(null);
    } else {
      onSortChange({ column: columnKey, direction: newDirection });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        openFilterColumn &&
        !target.closest(`[data-column="${openFilterColumn}"]`) &&
        !target.closest(`[data-filter-dropdown="${openFilterColumn}"]`)
      ) {
        setOpenFilterColumn(null);
        setFilterSearch({});
        setDropdownPosition(null);
        scrollParentRef.current = null;
      }
    };

    if (openFilterColumn) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openFilterColumn]);

  useEffect(() => {
    if (!openFilterColumn) {
      return;
    }

    const handleReposition = () => {
      updateDropdownPosition(openFilterColumn);
    };

    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    const scrollContainer = scrollParentRef.current;
    scrollContainer?.addEventListener('scroll', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
      scrollContainer?.removeEventListener('scroll', handleReposition);
    };
  }, [openFilterColumn, updateDropdownPosition]);

  const getCurrentFilterValue = (columnKey: string): string | string[] | null => {
    const filter = currentFilters.find(f => f.column === columnKey);
    return filter?.value || null;
  };

  const isValueSelected = (columnKey: string, value: string): boolean => {
    // Check pending selections first (if filter is open), otherwise check applied filters
    if (openFilterColumn === columnKey && pendingFilterSelections[columnKey]) {
      return pendingFilterSelections[columnKey].includes(value);
    }
    
    const filterValue = getCurrentFilterValue(columnKey);
    if (!filterValue) return false;
    if (Array.isArray(filterValue)) {
      return filterValue.includes(value);
    }
    return filterValue === value;
  };

  const getCurrentDateRangeFilter = (columnKey: string): { from: string | null; to: string | null } | null => {
    const filter = currentFilters.find(f => f.column === columnKey);
    return filter?.dateRange || null;
  };

  const getSortDirection = (columnKey: string): SortDirection => {
    if (currentSort?.column === columnKey) {
      return currentSort.direction;
    }
    return null;
  };

  const headerCells = columns.map((column) => {
    const currentFilter = getCurrentFilterValue(column.key);
    const currentDateRange = getCurrentDateRangeFilter(column.key);
    const sortDirection = getSortDirection(column.key);
    const hasActiveFilter = (currentFilter !== null && 
      (Array.isArray(currentFilter) ? currentFilter.length > 0 : true)) || 
      currentDateRange !== null;
    const isSortActive = sortDirection !== null;
    const isFilterOpen = openFilterColumn === column.key;
    const filterValues = getFilteredValues(column.key);

    return (
      <HeaderCell 
        key={column.key}
        data-column={column.key}
        ref={(el) => { headerRefs.current[column.key] = el; }}
      >
        <HeaderContent>
          <HeaderLabel>
            {(() => {
              const words = column.label.split(' ');
              // Only break if there are exactly 2 words
              if (words.length === 2) {
                return (
                  <>
                    {words[0]}
                    <br />
                    {words[1]}
                  </>
                );
              }
              // Otherwise, return the original label
              return column.label;
            })()}
          </HeaderLabel>
          <HeaderActions>
            {column.filterable && (
              <FilterIcon
                active={hasActiveFilter}
                onClick={() => handleFilterToggle(column.key)}
                title={hasActiveFilter ? `${t('storeFilter.clear')} filter` : t('storeFilter.filter')}
              >
                <Filter size={14} />
                {hasActiveFilter && (
                  <FilterBadge>
                    {Array.isArray(currentFilter) ? currentFilter.length : 1}
                  </FilterBadge>
                )}
              </FilterIcon>
            )}
            {column.sortable && (
              <SortButton
                active={isSortActive}
                direction={sortDirection}
                onClick={() => handleSortToggle(column.key)}
                title={
                  sortDirection === 'asc' ? t('storeFilter.sortAsc') :
                  sortDirection === 'desc' ? t('storeFilter.sortDesc') :
                  t('storeFilter.sort')
                }
              >
                {sortDirection === 'asc' ? (
                  <ChevronUp size={14} />
                ) : sortDirection === 'desc' ? (
                  <ChevronDown size={14} />
                ) : (
                  <ArrowUpDown size={14} />
                )}
              </SortButton>
            )}
          </HeaderActions>
        </HeaderContent>

        {column.filterable && isFilterOpen && dropdownPosition && typeof window !== 'undefined' && typeof document !== 'undefined' && createPortal(
          <FilterDropdown
            isOpen={isFilterOpen}
            top={dropdownPosition.top}
            left={dropdownPosition.left}
            minWidth={column.filterType === 'dateRange' ? 320 : Math.max(dropdownPosition.width, 200)}
            data-filter-dropdown={column.key}
            onClick={(e) => e.stopPropagation()}
          >
            {column.filterType === 'dateRange' ? (
              <DateRangeContainer>
                <DateRangeRow>
                  <DateRangeLabel>{t('storeFilter.fromDate', 'From Date')}</DateRangeLabel>
                  <DateInput
                    type="date"
                    value={dateRangeFilters[column.key]?.from || currentDateRange?.from || ''}
                    onChange={(e) => {
                      const currentRange = dateRangeFilters[column.key] || { from: '', to: '' };
                      handleDateRangeChange(column.key, e.target.value, currentRange.to);
                    }}
                  />
                </DateRangeRow>
                <DateRangeRow>
                  <DateRangeLabel>{t('storeFilter.toDate', 'To Date')}</DateRangeLabel>
                  <DateInput
                    type="date"
                    value={dateRangeFilters[column.key]?.to || currentDateRange?.to || ''}
                    min={dateRangeFilters[column.key]?.from || currentDateRange?.from || undefined}
                    onChange={(e) => {
                      const currentRange = dateRangeFilters[column.key] || { from: '', to: '' };
                      handleDateRangeChange(column.key, currentRange.from, e.target.value);
                    }}
                  />
                </DateRangeRow>
                <DateRangeButtons>
                  <ClearFilterButton onClick={() => clearDateRangeFilter(column.key)}>
                    {t('storeFilter.clear')}
                  </ClearFilterButton>
                  <ClearFilterButton
                    onClick={() => applyDateRangeFilter(column.key)}
                    style={{
                      backgroundColor: ThemeColors.primary.main,
                      color: 'white',
                      borderColor: ThemeColors.primary.main
                    }}
                  >
                    {t('storeFilter.apply', 'Apply')}
                  </ClearFilterButton>
                </DateRangeButtons>
                {currentDateRange && (
                  <div style={{ 
                    fontSize: Typography.fontSize.xs, 
                    color: ThemeColors.success.main,
                    padding: `${Spacing.xs} 0`,
                    textAlign: 'center'
                  }}>
                    {t('storeFilter.filterActive', 'Filter active')}
                  </div>
                )}
              </DateRangeContainer>
            ) : (
              <>
            <FilterSearch
              type="text"
              placeholder={t('storeFilter.searchValues')}
              value={filterSearch[column.key] || ''}
              onChange={(e) => {
                const searchValue = e.target.value;
                setFilterSearch({ ...filterSearch, [column.key]: searchValue });
                
                // Debounce search to avoid excessive database queries
                // Clear any existing timeout
                const timeoutId = (window as any)[`filterSearchTimeout_${column.key}`];
                if (timeoutId) {
                  clearTimeout(timeoutId);
                }
                
                // When user types, query database with search term
                // This ensures search works across entire database, not just displayed stores
                if (searchValue.trim()) {
                  // Debounce: wait 300ms after user stops typing before querying
                  (window as any)[`filterSearchTimeout_${column.key}`] = setTimeout(() => {
                    console.log(`[SortableFilterableTableHeader] User searching "${searchValue}" - querying DATABASE for ${column.key}`);
                    loadFilterValuesFromDb(column.key, searchValue.trim(), false);
                    
                    // When search results load, clear pending selections so "All [search term]" is unselected
                    // User can then click "All [search term]" to select all results
                    setPendingFilterSelections(prev => ({
                      ...prev,
                      [column.key]: []
                    }));
                  }, 300);
                } else {
                  // If search is cleared, reload all values (without search term)
                  console.log(`[SortableFilterableTableHeader] Search cleared - reloading all values for ${column.key}`);
                  loadFilterValuesFromDb(column.key, undefined, false);
                  
                  // Clear pending selections when search is cleared
                  setPendingFilterSelections(prev => ({
                    ...prev,
                    [column.key]: []
                  }));
                }
              }}
            />
            {loadingFilterValues[column.key] && (
              <div style={{ 
                padding: Spacing.md, 
                textAlign: 'center', 
                color: ThemeColors.primary.main,
                fontSize: Typography.fontSize.sm 
              }}>
                Loading all values from database...
                {filterSearch[column.key] && (
                  <div style={{ fontSize: Typography.fontSize.xs, marginTop: Spacing.xs, color: ThemeColors.text.secondary }}>
                    Searching across entire database, not just displayed stores
                  </div>
                )}
              </div>
            )}
            {filterValues.length === 0 && !loadingFilterValues[column.key] && filterSearch[column.key] && (
              <div style={{ 
                padding: Spacing.md, 
                textAlign: 'center', 
                color: ThemeColors.text.secondary,
                fontSize: Typography.fontSize.sm 
              }}>
                No matches found. Make sure database values are loaded.
              </div>
            )}
            <FilterOptions>
              <FilterOption
                selected={
                  openFilterColumn === column.key 
                    ? (() => {
                        const searchTerm = filterSearch[column.key]?.trim();
                        const hasSearch = !!searchTerm && filterValues.length > 0;
                        const pendingCount = pendingFilterSelections[column.key]?.length || 0;
                        
                        if (hasSearch) {
                          // When searching, "All [search term]" is selected if all filtered results are selected
                          return pendingCount === filterValues.length && filterValues.length > 0;
                        } else {
                          // When not searching, "All" is selected if no selections
                          return pendingCount === 0;
                        }
                      })()
                    : currentFilter === null && !currentDateRange
                }
                onClick={() => handleFilterSelect(column.key, null)}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={
                      openFilterColumn === column.key 
                        ? (() => {
                            const searchTerm = filterSearch[column.key]?.trim();
                            const hasSearch = !!searchTerm && filterValues.length > 0;
                            const pendingCount = pendingFilterSelections[column.key]?.length || 0;
                            
                            if (hasSearch) {
                              // When searching, "All [search term]" is checked if all filtered results are selected
                              return pendingCount === filterValues.length && filterValues.length > 0;
                            } else {
                              // When not searching, "All" is checked if no selections
                              return pendingCount === 0;
                            }
                          })()
                        : currentFilter === null && !currentDateRange
                    }
                    onChange={() => {}}
                    style={{ cursor: 'pointer' }}
                  />
                  {(() => {
                    const searchTerm = filterSearch[column.key]?.trim();
                    const hasSearch = !!searchTerm && filterValues.length > 0;
                    if (hasSearch) {
                      return `All "${searchTerm}"`;
                    }
                    return t('storeFilter.all');
                  })()}
                </span>
              </FilterOption>
              {filterValues.map((value) => {
                const isSelected = isValueSelected(column.key, value);
                return (
                <FilterOption
                  key={value}
                    selected={isSelected}
                  onClick={() => handleFilterSelect(column.key, value)}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFilterSelect(column.key, value);
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ flex: 1 }}>{value}</span>
                    </span>
                </FilterOption>
                );
              })}
            </FilterOptions>
            {filterValues.length === 0 && !loadingFilterValues[column.key] && (
              <FilterFooter>
                <span style={{ fontSize: Typography.fontSize.xs, color: ThemeColors.text.secondary }}>
                  {t('storeFilter.noValues')}
                </span>
              </FilterFooter>
            )}
            <FilterFooter>
              <ClearFilterButton onClick={() => {
                setPendingFilterSelections(prev => ({
                  ...prev,
                  [column.key]: []
                }));
              }}>
                {t('storeFilter.clear')}
              </ClearFilterButton>
              <ClearFilterButton
                onClick={() => applyPendingFilter(column.key)}
                style={{
                  backgroundColor: ThemeColors.primary.main,
                  color: 'white',
                  borderColor: ThemeColors.primary.main,
                  flex: 1
                }}
              >
                {t('storeFilter.apply', 'Apply')}
                {(pendingFilterSelections[column.key]?.length || 0) > 0 && 
                  ` (${pendingFilterSelections[column.key]?.length || 0})`
                }
              </ClearFilterButton>
            </FilterFooter>
              </>
            )}
          </FilterDropdown>,
          document.body
        )}
      </HeaderCell>
    );
  });

  // If renderAsRow is true, return just the cells (fragments) to be placed inside an existing <tr>
  if (renderAsRow) {
    return <>{headerCells}</>;
  }

  // Otherwise, return the full <thead><tr> structure
  return (
    <thead>
      <tr>
        {headerCells}
      </tr>
    </thead>
  );
};

